// Stap 2: persoonlijke gegevens. PRD-06 §4 US-ONB-02.

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { OnboardingProgress } from "../OnboardingWizard";

interface Props {
  progress: OnboardingProgress;
  onBack: () => void;
  onAdvance: () => void;
}

export function PersonalDataStep({ onBack, onAdvance }: Props) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [withAddress, setWithAddress] = useState(false);
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api.post("/v1/onboarding/personal-data", {
        firstName,
        lastName,
        phone: phone || undefined,
        address: withAddress ? { street, houseNumber, postalCode, city } : undefined,
      }),
    onSuccess: () => onAdvance(),
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorKey(null);
    submit.mutate();
  }

  return (
    <article className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
      <h2 className="text-2xl font-semibold mb-2">
        {t("onboarding.personal.title", "Persoonlijke gegevens")}
      </h2>
      <p className="text-neutral-600 mb-6">
        {t("onboarding.personal.subtitle", "Vertel ons hoe we je kunnen aanspreken.")}
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="fn" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("onboarding.personal.firstName", "Voornaam")} *
            </label>
            <input
              id="fn"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="ln" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("onboarding.personal.lastName", "Achternaam")} *
            </label>
            <input
              id="ln"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
            />
          </div>
        </div>

        <div>
          <label htmlFor="ph" className="block text-sm font-medium text-neutral-700 mb-1">
            {t("onboarding.personal.phone", "Telefoonnummer")}{" "}
            <span className="text-neutral-400 text-xs">
              ({t("onboarding.personal.optional", "optioneel")})
            </span>
          </label>
          <input
            id="ph"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="06xxxxxxxx"
            autoComplete="tel"
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer pt-2">
          <input
            type="checkbox"
            checked={withAddress}
            onChange={(e) => setWithAddress(e.target.checked)}
          />
          <span className="text-sm text-neutral-700">
            {t("onboarding.personal.addAddress", "Ik wil mijn adres ook invullen")}
          </span>
        </label>

        {withAddress && (
          <fieldset className="border border-neutral-200 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("onboarding.personal.street", "Straat")} *
                </label>
                <input
                  type="text"
                  required={withAddress}
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  autoComplete="address-line1"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("onboarding.personal.houseNumber", "Nr")} *
                </label>
                <input
                  type="text"
                  required={withAddress}
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("onboarding.personal.postalCode", "Postcode")} *
                </label>
                <input
                  type="text"
                  required={withAddress}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
                  placeholder="1234 AB"
                  pattern="[0-9]{4}\s?[A-Z]{2}"
                  autoComplete="postal-code"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("onboarding.personal.city", "Plaats")} *
                </label>
                <input
                  type="text"
                  required={withAddress}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  autoComplete="address-level2"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
            </div>
          </fieldset>
        )}

        {errorKey && (
          <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            {t(errorKey)}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2.5 text-neutral-700 hover:bg-neutral-100 rounded-lg"
          >
            ← {t("back", "Terug")}
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
          >
            {submit.isPending ? t("loading", "Laden…") : t("onboarding.continue", "Doorgaan →")}
          </button>
        </div>
      </form>
    </article>
  );
}
